// angular
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// services
import { OrganizationService } from '@core/services';

// addons
import { catchError, filter, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';

// store
import { Action, select, Store } from '@ngrx/store';
import * as fromStore from '@store/index';
import { State } from '@store/index';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as actions from './actions';
import * as selectors from './selectors';
import * as tradesActions from '../trades/actions';

// models
import { IOrganization, OrganizationStatusEnum } from '@shared/models';

@Injectable()
export class OrganizationEffects {
  loadOrganization$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.Load),
    mergeMap(({ id }: { id: number }) => {
      return this.organizationService.getOrganization(id).pipe(
        map((organization: IOrganization) => actions.loadSuccess({ organization })),
        catchError(error => of(actions.loadFail({ error })))
      );
    })
  ));

  loadTradesAfterOrganization$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.LoadSuccess),
    filter(({ organization }: { organization: IOrganization }) => organization.status === OrganizationStatusEnum.Approved),
    map(({ organization: { id }}: { organization: IOrganization }) => {
      return tradesActions.loadPerOrganization({ status: null });
    })
  ));

  declineOrganization$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.Decline),
    tap(({ id }) => {
      this.store.dispatch(fromStore.OrganizationActions.load({ id: +id }));
      this.router.navigate([], {
        queryParams: {
          status: OrganizationStatusEnum.Declined
        },
        fragment: String(id)
      });
    })
  ), { dispatch: false });

  approveOrganization$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.Approve),
    withLatestFrom(this.store.pipe(select(selectors.getOrganization))),
    mergeMap(([_, { id }]: [Action, IOrganization]) => {
      return this.organizationService.approveOrganization(id).pipe(
        tap(() => {
          this.store.dispatch(fromStore.OrganizationActions.load({ id: +id }));
          this.router.navigate([], {
            queryParams: {
              status: OrganizationStatusEnum.Approved
            },
            fragment: String(id)
          });
        })
      );
    })
  ), { dispatch: false });

  removeOrganization$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.Remove),
    withLatestFrom(this.store.pipe(select(selectors.getOrganization))),
    mergeMap(([_, { id }]: [Action, IOrganization]) => {
      return this.organizationService.removeOrganization(id).pipe(
        tap(() => {
          this.store.dispatch(fromStore.OrganizationActions.clear());
          this.store.dispatch(fromStore.OrganizationsActions.load({ status: null }));
          this.router.navigate([], {
            queryParamsHandling: 'merge',
            relativeTo: this.route,
            fragment: null
          });
          this.router.navigate([], {});
        })
      );
    })
  ), { dispatch: false });

  enableMfa$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.EnableMfa),
    withLatestFrom(this.store.pipe(select(selectors.getOrganization))),
    mergeMap(([_, { id }]: [Action, IOrganization]) => {
      return this.organizationService.enableMfa(id).pipe(
        tap(() => {
          this.store.dispatch(fromStore.OrganizationActions.load({ id: +id }));
          // this.router.navigate([], {
          //   queryParams: {
          //     status: OrganizationStatusEnum.Approved
          //   },
          //   fragment: String(id)
          // });
        })
      );
    })
  ), { dispatch: false });

  disableMfa$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.DisableMfa),
    withLatestFrom(this.store.pipe(select(selectors.getOrganization))),
    mergeMap(([_, { id }]: [Action, IOrganization]) => {
      return this.organizationService.disableMfa(id).pipe(
        tap(() => {
          this.store.dispatch(fromStore.OrganizationActions.load({ id: +id }));
          // this.router.navigate([], {
          //   queryParams: {
          //     status: OrganizationStatusEnum.Approved
          //   },
          //   fragment: String(id)
          // });
        })
      );
    })
  ), { dispatch: false });

  enableMfaTestMode$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.EnableMfaTestMode),
    withLatestFrom(this.store.pipe(select(selectors.getOrganization))),
    mergeMap(([_, { id }]: [Action, IOrganization]) => {
      return this.organizationService.enableMfaTestMode(id).pipe(
        tap(() => {
          this.store.dispatch(fromStore.OrganizationActions.load({ id: +id }));
        })
      );
    })
  ), { dispatch: false });

  disableMfaTestMode$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.DisableMfaTestMode),
    withLatestFrom(this.store.pipe(select(selectors.getOrganization))),
    mergeMap(([_, { id }]: [Action, IOrganization]) => {
      return this.organizationService.disableMfaTestMode(id).pipe(
        tap(() => {
          this.store.dispatch(fromStore.OrganizationActions.load({ id: +id }));
        })
      );
    })
  ), { dispatch: false });


  constructor(
    private actions$: Actions,
    private organizationService: OrganizationService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<State>
  ) {}
}
